import { useState } from "react";
import * as Y from "yjs";
import { useYDoc } from "../hooks/useYDoc";
import { DeleteButton } from "./DeleteButton";
import { Button } from "./ui/Button";
import { ReminderEdit } from "./ReminderEdit";
import { DateLabel } from "./DateLabel";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Spinner } from "./ui/Spinner";
import { EmptyState } from "./ui/EmptyState";

const TODAY_DOC_ID = "today";
const DONE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface Reminder {
  id: string;
  text: string;
  date: string;
  time: string; // HH:MM, default "00:00"
  done: boolean;
  completedAt?: number;
}

function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function TodayTab({ syncVersion }: { syncVersion?: number }) {
  const { doc, loading, error } = useYDoc(TODAY_DOC_ID, syncVersion);
  const [newText, setNewText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ index: number; text: string } | null>(null);

  if (loading) return <div className="container"><Spinner /></div>;
  if (error) return <div className="container"><p>Error: {error}</p></div>;
  if (!doc) return null;

  const yReminders = doc.getArray("reminders");
  const reminders = yReminders.toJSON() as Reminder[];
  const today = todayStr();

  const findIndex = (id: string): number => {
    for (let i = 0; i < yReminders.length; i++) {
      const item = yReminders.get(i) as Y.Map<unknown>;
      if (item.get("id") === id) return i;
    }
    return -1;
  };

  // If editing a reminder, show the edit screen
  if (editingId) {
    const reminder = reminders.find((r) => r.id === editingId);
    if (!reminder) {
      setEditingId(null);
      return null;
    }

    return (
      <ReminderEdit
        text={reminder.text}
        date={reminder.date}
        time={reminder.time ?? "00:00"}
        onSave={(text, date, time) => {
          const idx = findIndex(editingId);
          if (idx !== -1) {
            doc.transact(() => {
              const item = yReminders.get(idx) as Y.Map<unknown>;
              item.set("text", text);
              item.set("date", date);
              item.set("time", time);
            });
          }
          setEditingId(null);
        }}
        onClose={() => setEditingId(null)}
      />
    );
  }

  const nowMs = Date.now();
  const nowTime = new Date().toTimeString().slice(0, 5); // HH:MM
  const isDue = (r: Reminder) => r.date < today || (r.date === today && (r.time ?? "00:00") <= nowTime);
  const byDateTime = (a: Reminder, b: Reminder) =>
    a.date.localeCompare(b.date) || (a.time ?? "00:00").localeCompare(b.time ?? "00:00");
  const active = reminders.filter((r) => !r.done);
  const todayItems = active.filter((r) => r.date <= today).sort(byDateTime);
  const upcomingItems = active.filter((r) => r.date > today).sort(byDateTime);
  // Prune expired done items at read time only; never mutate the CRDT on load
  // to avoid ping-pong writes across devices. Items lacking completedAt are
  // legacy entries and are kept until the user explicitly deletes them.
  const doneItems = reminders.filter((r) => {
    if (!r.done) return false;
    if (typeof r.completedAt !== "number") return true;
    return nowMs - r.completedAt <= DONE_TTL_MS;
  });

  const addReminder = () => {
    const text = newText.trim();
    if (!text) return;
    doc.transact(() => {
      const item = new Y.Map<unknown>();
      item.set("id", crypto.randomUUID());
      item.set("text", text);
      item.set("date", today);
      item.set("time", "00:00");
      item.set("done", false);
      yReminders.push([item]);
    });
    setNewText("");
  };

  const toggleReminder = (index: number) => {
    doc.transact(() => {
      const item = yReminders.get(index) as Y.Map<unknown>;
      const wasDone = item.get("done");
      item.set("done", !wasDone);
      if (!wasDone) {
        item.set("completedAt", Date.now());
      } else {
        item.delete("completedAt");
      }
    });
  };

  const deleteReminder = (index: number) => {
    doc.transact(() => {
      yReminders.delete(index, 1);
    });
  };

  const renderItem = (item: Reminder) => {
    const idx = findIndex(item.id);
    if (idx === -1) return null;
    return (
      <li key={item.id} className="list-item">
        <input
          type="checkbox"
          checked={item.done}
          onChange={() => toggleReminder(idx)}
          className="checkbox"
        />
        <span
          className={`list-item-text ${item.done ? "checked" : ""}`}
          onClick={() => setEditingId(item.id)}
          style={{ cursor: "pointer", flex: 1 }}
        >
          {item.text}
          {(!isDue(item) || (item.time && item.time !== "00:00")) && (
            <DateLabel date={item.date} time={item.time} className="date-badge" />
          )}
        </span>
        <DeleteButton onClick={() => setConfirmDelete({ index: idx, text: item.text })} />
      </li>
    );
  };

  return (
    <div className="container">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          addReminder();
        }}
        className="add-form"
      >
        <input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Add a reminder..."
          className="add-input"
        />
        <Button type="submit">Add</Button>
      </form>

      <h2 className="section-title">Today</h2>

      {todayItems.length > 0 && (
        <ul className="item-list">
          {todayItems.map(renderItem)}
        </ul>
      )}

      {upcomingItems.length > 0 && (
        <>
          <h3 className="subsection-title">Upcoming</h3>
          <ul className="item-list">
            {upcomingItems.map(renderItem)}
          </ul>
        </>
      )}

      {doneItems.length > 0 && (
        <>
          <h3 className="subsection-title" style={{ opacity: 0.5 }}>Done</h3>
          <ul className="item-list">
            {doneItems.map(renderItem)}
          </ul>
        </>
      )}

      {reminders.length === 0 && (
        <EmptyState>Nothing for today. Add a reminder above.</EmptyState>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        message={confirmDelete ? `Delete "${confirmDelete.text}"?` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (confirmDelete) {
            deleteReminder(confirmDelete.index);
            setConfirmDelete(null);
          }
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
